/**
 * Portfolio calculation utilities
 * Calculate total value, P/L, current value using sell prices
 */

import type { GoldAsset } from '@/types/asset'
import type { SaleTransaction } from '@/types/sale'
import type { GoldPriceHistory } from '@/types/price'

export interface PortfolioSnapshot {
  date: string
  totalValue: number
  totalCost: number
  profitLoss: number
  profitLossPercent: number
}

export interface PortfolioSummary {
  totalAssets: number
  totalCost: number
  currentValue: number
  totalProfitLoss: number
  profitLossPercent: number
  totalRevenue: number
}

/**
 * Calculate current portfolio value using latest sell prices
 */
export function calculateCurrentPortfolioValue(
  assets: GoldAsset[],
  latestPrices: Map<number, GoldPriceHistory>
): number {
  return assets
    .filter((asset) => asset.status === 'holding')
    .reduce((total, asset) => {
      const price = latestPrices.get(asset.goldTypeId)
      if (price) {
        return total + asset.quantity * price.sellPrice
      }
      // Fallback to purchase price if no current price available
      return total + asset.quantity * asset.purchasePrice
    }, 0)
}

/**
 * Calculate total cost of all assets
 */
export function calculateTotalCost(assets: GoldAsset[]): number {
  return assets.reduce((total, asset) => {
    return total + asset.quantity * asset.purchasePrice
  }, 0)
}

/**
 * Calculate total revenue from sales
 */
export function calculateTotalRevenue(sales: SaleTransaction[]): number {
  return sales.reduce((total, sale) => total + sale.revenue, 0)
}

/**
 * Calculate portfolio summary
 */
export function calculatePortfolioSummary(
  assets: GoldAsset[],
  sales: SaleTransaction[],
  latestPrices: Map<number, GoldPriceHistory>
): PortfolioSummary {
  const totalCost = calculateTotalCost(assets)
  const currentValue = calculateCurrentPortfolioValue(assets, latestPrices)
  const totalRevenue = calculateTotalRevenue(sales)
  const totalProfitLoss = currentValue + totalRevenue - totalCost
  const profitLossPercent = totalCost > 0 ? (totalProfitLoss / totalCost) * 100 : 0

  return {
    totalAssets: assets.length,
    totalCost,
    currentValue,
    totalProfitLoss,
    profitLossPercent,
    totalRevenue,
  }
}

/**
 * Calculate portfolio snapshots over time for charting
 */
export function calculatePortfolioSnapshots(
  assets: GoldAsset[],
  sales: SaleTransaction[],
  priceHistory: GoldPriceHistory[]
): PortfolioSnapshot[] {
  // Group prices by date
  const pricesByDate = new Map<string, Map<number, GoldPriceHistory>>()
  
  priceHistory.forEach((price) => {
    if (!pricesByDate.has(price.date)) {
      pricesByDate.set(price.date, new Map())
    }
    pricesByDate.get(price.date)!.set(price.goldTypeId, price)
  })

  // Get all unique dates, sorted
  const dates = Array.from(pricesByDate.keys()).sort()

  // Calculate snapshot for each date
  return dates.map((date) => {
    const pricesOnDate = pricesByDate.get(date)!
    
    // Calculate total value using prices on this date
    const totalValue = assets
      .filter((asset) => asset.status === 'holding')
      .reduce((total, asset) => {
        const price = pricesOnDate.get(asset.goldTypeId)
        if (price) {
          return total + asset.quantity * price.sellPrice
        }
        return total
      }, 0)

    // Calculate total cost (only for assets purchased before or on this date)
    const totalCost = assets
      .filter((asset) => new Date(asset.purchaseDate) <= new Date(date))
      .reduce((total, asset) => total + asset.quantity * asset.purchasePrice, 0)

    // Calculate revenue from sales up to this date
    const revenueUpToDate = sales
      .filter((sale) => new Date(sale.saleDate) <= new Date(date))
      .reduce((total, sale) => total + sale.revenue, 0)

    const profitLoss = totalValue + revenueUpToDate - totalCost
    const profitLossPercent = totalCost > 0 ? (profitLoss / totalCost) * 100 : 0

    return {
      date,
      totalValue,
      totalCost,
      profitLoss,
      profitLossPercent,
    }
  })
}

/**
 * Calculate cumulative P/L over time
 */
export function calculateCumulativePL(
  assets: GoldAsset[],
  sales: SaleTransaction[],
  priceHistory: GoldPriceHistory[]
): Array<{ date: string; profitLoss: number }> {
  const snapshots = calculatePortfolioSnapshots(assets, sales, priceHistory)
  return snapshots.map((snapshot) => ({
    date: snapshot.date,
    profitLoss: snapshot.profitLoss,
  }))
}

